import React, { useMemo } from "react";
import BaseTableAction from "../../Base/BaseTableAction";
import { ServiceEmailStatus } from "../../../models/ServiceEmailRequestModel";
import { ServiceEmailSolicitud } from "../../../models/ServiceEmailSolicitudModel";
import {
  MonitorMode,
  OPS_STATUS_LABEL,
  OpsStatus,
  buildMonitorAlerts,
  computeMonitorStats,
  formatDayLabel,
  formatServiceDate,
  formatTime,
  getActiveLinks,
  getAssigned,
  getRemaining,
  resolveOpsStatus,
  todayIso,
} from "./emailsMonitorUtils";
import "./styles/ServicesTable.css";
import "./styles/ServiceEmailsTable.css";
import "./styles/ServiceEmailSolicitudesTable.css";

export type ServiceEmailSolicitudesTableFilters = {
  search: string;
  email_status: "" | ServiceEmailStatus;
  /** Día del servicio (un solo día; no rango). */
  service_date: string;
  pending_only?: boolean;
  missing_driver_only?: boolean;
  // Filtros específicos
  origin?: string;
  destination?: string;
  passengers?: string;
  time_from?: string;
  time_to?: string;
};

type Props = {
  data: ServiceEmailSolicitud[];
  totalCount?: number;
  loading?: boolean;
  filters: ServiceEmailSolicitudesTableFilters;
  mode?: MonitorMode;
  lastUpdatedAt?: Date | null;
  live?: boolean;
  dayLabel?: string;
  kpiSinServicio?: number;
  onModeChange?: (mode: MonitorMode) => void;
  onRefresh?: () => void;
  onFiltersChange: (filters: ServiceEmailSolicitudesTableFilters) => void;
  onOpenEmail: (emailId: number) => void;
};

const STATUS_LABELS: Record<ServiceEmailStatus, string> = {
  PENDIENTE: "Pendiente",
  AGREGADO: "Agregado",
};

const EditIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L9.75 16.902 6 18l1.098-3.75L16.862 4.487z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ServiceEmailSolicitudesTable: React.FC<Props> = ({
  data,
  totalCount,
  loading = false,
  filters,
  mode = "servicio",
  lastUpdatedAt = null,
  live = false,
  dayLabel,
  kpiSinServicio,
  onModeChange,
  onRefresh,
  onFiltersChange,
  onOpenEmail,
}) => {
  const resultsCount = totalCount ?? data.length;
  const resolvedDayLabel = dayLabel || formatDayLabel(filters.service_date);
  const stats = useMemo(() => computeMonitorStats(data), [data]);
  const alerts = useMemo(
    () =>
      buildMonitorAlerts(stats, {
        dayLabel: resolvedDayLabel,
        kpiSinServicio,
      }),
    [stats, resolvedDayLabel, kpiSinServicio]
  );

  const updateFilter = <K extends keyof ServiceEmailSolicitudesTableFilters>(
    key: K,
    value: ServiceEmailSolicitudesTableFilters[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () =>
    onFiltersChange({
      search: "",
      email_status: "",
      service_date: todayIso(),
      pending_only: false,
      missing_driver_only: false,
      origin: "",
      destination: "",
      passengers: "",
      time_from: "",
      time_to: "",
    });

  const focusToday = () => {
    onFiltersChange({
      ...filters,
      service_date: todayIso(),
      pending_only: false,
      missing_driver_only: false,
    });
  };

  const hasFilters = Boolean(
    filters.search ||
      filters.email_status ||
      (filters.service_date && filters.service_date !== todayIso()) ||
      filters.pending_only ||
      filters.missing_driver_only ||
      filters.origin ||
      filters.destination ||
      filters.passengers ||
      filters.time_from ||
      filters.time_to
  );

  const visibleData = useMemo(() => {
    let rows = data;
    if (filters.pending_only) {
      rows = rows.filter((item) => {
        const status = resolveOpsStatus(item);
        return status === "sin_servicio" || status === "parcial";
      });
    }
    if (filters.missing_driver_only) {
      rows = rows.filter((item) => resolveOpsStatus(item) === "sin_conductor");
    }
    if (mode === "conductor") {
      // En modo conductor priorizamos filas que ya tienen vínculo
      rows = [...rows].sort((a, b) => {
        const rank = (item: ServiceEmailSolicitud) => {
          const s = resolveOpsStatus(item);
          if (s === "sin_conductor") return 0;
          if (s === "parcial") return 1;
          if (s === "sin_servicio") return 2;
          if (s === "listo") return 3;
          return 4;
        };
        return rank(a) - rank(b);
      });
    } else {
      rows = [...rows].sort((a, b) => {
        const rank = (item: ServiceEmailSolicitud) => {
          const s = resolveOpsStatus(item);
          if (s === "sin_servicio") return 0;
          if (s === "parcial") return 1;
          if (s === "sin_conductor") return 2;
          if (s === "listo") return 3;
          return 4;
        };
        return rank(a) - rank(b);
      });
    }
    return rows;
  }, [data, filters.pending_only, filters.missing_driver_only, mode]);

  const renderOpsBadge = (status: OpsStatus) => (
    <span className={`service-email-ops service-email-ops--${status}`}>
      <span className="service-email-ops-pulse" aria-hidden />
      {OPS_STATUS_LABEL[status]}
    </span>
  );

  const renderServiceCell = (item: ServiceEmailSolicitud) => {
    const links = getActiveLinks(item);
    const remaining = getRemaining(item);
    if (!links.length) {
      return (
        <div className="service-email-ops-block">
          <span className="service-email-ops-msg service-email-ops-msg--danger">
            Falta agendar servicio
          </span>
          <span className="service-email-ops-sub">{remaining} pax sin cubrir</span>
        </div>
      );
    }
    return (
      <div className="service-email-ops-block">
        <div className="service-email-link-chips">
          {links.slice(0, 3).map((link) => (
            <span key={link.id} className="service-email-link-chip" title={`Servicio #${link.service_id}`}>
              #{link.service_id}
            </span>
          ))}
          {links.length > 3 ? (
            <span className="service-email-link-chip service-email-link-chip--more">
              +{links.length - 3}
            </span>
          ) : null}
        </div>
        {remaining > 0 ? (
          <span className="service-email-ops-msg service-email-ops-msg--warning">
            Faltan {remaining} pax por asignar
          </span>
        ) : (
          <span className="service-email-ops-msg service-email-ops-msg--ok">Cupo completo</span>
        )}
      </div>
    );
  };

  const renderDriverCell = (item: ServiceEmailSolicitud) => {
    const links = getActiveLinks(item);
    if (!links.length) {
      return (
        <div className="service-email-ops-block">
          <span className="service-email-ops-msg service-email-ops-msg--danger">
            Sin servicio agendado
          </span>
          <span className="service-email-ops-sub">Asigna un servicio antes del conductor</span>
        </div>
      );
    }

    return (
      <div className="service-email-ops-block">
        {links.map((link) => {
          const driver = String(link.driver_name || "").trim();
          return (
            <div key={link.id} className="service-email-driver-row">
              <span className="service-email-link-chip">#{link.service_id}</span>
              {driver ? (
                <span className="service-email-driver-name" title={driver}>
                  {driver}
                </span>
              ) : (
                <span className="service-email-ops-msg service-email-ops-msg--warning">
                  Sin conductor
                </span>
              )}
              {link.service_status_label ? (
                <span className="service-email-status-mini">{link.service_status_label}</span>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="service-emails-list service-email-solicitudes-list service-email-monitor">
      <div className="service-email-monitor-strip" aria-live="polite">
        <div className="service-email-monitor-live">
          <span className={`service-email-live-dot${live ? " is-live" : ""}`} aria-hidden />
          <div>
            <strong>
              {mode === "conductor" ? "Modo conductor" : "Modo servicio agendado"}
            </strong>
            <span>
              Día {resolvedDayLabel} · {live ? "monitoreo en vivo" : "solicitudes"} ·{" "}
              {lastUpdatedAt
                ? lastUpdatedAt.toLocaleTimeString("es-CL", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "—"}
            </span>
          </div>
        </div>
        <div className="service-email-monitor-metrics">
          <div className="service-email-monitor-metric service-email-monitor-metric--critical">
            <span>Sin servicio</span>
            <strong>{stats.sinServicio + stats.parcial}</strong>
          </div>
          <div className="service-email-monitor-metric service-email-monitor-metric--warning">
            <span>Sin conductor</span>
            <strong>{stats.sinConductor}</strong>
          </div>
          <div className="service-email-monitor-metric service-email-monitor-metric--ok">
            <span>Listas</span>
            <strong>{stats.listo}</strong>
          </div>
        </div>
        {onRefresh && (
          <button
            type="button"
            className="service-email-monitor-refresh"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? "Actualizando…" : "Actualizar"}
          </button>
        )}
      </div>

      {alerts.some((a) => a.tone !== "success") && (
        <div className="service-email-alert-banners" role="status">
          {alerts
            .filter((a) => a.tone !== "success")
            .map((alert) => (
              <div
                key={alert.id}
                className={`service-email-alert-banner service-email-alert-banner--${alert.tone}`}
              >
                <strong>{alert.title}</strong>
                <span>{alert.detail}</span>
              </div>
            ))}
        </div>
      )}

      <div className="service-emails-list-toolbar">
        <div className="service-emails-list-toolbar-top">
          <div className="service-emails-list-title">
            <span className="service-emails-list-count">
              {resultsCount} solicitud{resultsCount !== 1 ? "es" : ""}
            </span>
            {onModeChange && (
              <div className="service-email-mode-toggle" role="group" aria-label="Modo de tabla">
                <button
                  type="button"
                  className={mode === "servicio" ? "is-active" : ""}
                  aria-pressed={mode === "servicio"}
                  onClick={() => onModeChange("servicio")}
                >
                  Servicio
                </button>
                <button
                  type="button"
                  className={mode === "conductor" ? "is-active" : ""}
                  aria-pressed={mode === "conductor"}
                  onClick={() => onModeChange("conductor")}
                >
                  Conductor
                </button>
              </div>
            )}
          </div>
          <div className="service-email-toolbar-actions">
            <button type="button" className="service-emails-clear-filters" onClick={focusToday}>
              Solo hoy
            </button>
            {hasFilters && (
              <button className="service-emails-clear-filters" onClick={clearFilters} type="button">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        <div className="service-emails-filters-row">
          <div className="service-emails-search-wrap">
            <input
              id="service-email-solicitud-search"
              type="text"
              className="service-emails-search-input"
              placeholder="Buscar por asunto, emisor o ID…"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
            />
          </div>

          <div className="service-emails-date-filters" aria-label="Filtrar por día del servicio">
            <label className="service-emails-date-field">
              <span>Día del servicio</span>
              <input
                type="date"
                value={filters.service_date}
                onChange={(event) => updateFilter("service_date", event.target.value)}
              />
            </label>
          </div>

          <div className="service-emails-status-filters" role="group" aria-label="Filtrar solicitudes">
            <button
              type="button"
              className={`service-emails-status-chip pending-only${
                filters.pending_only ? " is-active" : ""
              }`}
              aria-pressed={Boolean(filters.pending_only)}
              onClick={() =>
                onFiltersChange({
                  ...filters,
                  pending_only: !filters.pending_only,
                  missing_driver_only: false,
                })
              }
            >
              Sin servicio
            </button>
            <button
              type="button"
              className={`service-emails-status-chip missing-driver${
                filters.missing_driver_only ? " is-active" : ""
              }`}
              aria-pressed={Boolean(filters.missing_driver_only)}
              onClick={() =>
                onFiltersChange({
                  ...filters,
                  missing_driver_only: !filters.missing_driver_only,
                  pending_only: false,
                })
              }
            >
              Sin conductor
            </button>
            {(Object.keys(STATUS_LABELS) as ServiceEmailStatus[]).map((status) => {
              const active = filters.email_status === status;
              return (
                <button
                  key={status}
                  type="button"
                  className={`service-emails-status-chip ${status.toLowerCase()}${
                    active ? " is-active" : ""
                  }`}
                  aria-pressed={active}
                  onClick={() => updateFilter("email_status", active ? "" : status)}
                >
                  {STATUS_LABELS[status]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="service-emails-advanced-filters">
          <div className="service-emails-advanced-filters-grid">
            <label className="service-email-filter-field">
              <span className="service-email-filter-label">Origen</span>
              <input
                type="text"
                className="service-email-filter-input"
                placeholder="Filtrar por origen…"
                value={filters.origin || ""}
                onChange={(event) => updateFilter("origin", event.target.value)}
              />
            </label>

            <label className="service-email-filter-field">
              <span className="service-email-filter-label">Destino</span>
              <input
                type="text"
                className="service-email-filter-input"
                placeholder="Filtrar por destino…"
                value={filters.destination || ""}
                onChange={(event) => updateFilter("destination", event.target.value)}
              />
            </label>

            <label className="service-email-filter-field">
              <span className="service-email-filter-label">Pasajeros</span>
              <input
                type="text"
                className="service-email-filter-input"
                placeholder="Filtrar por nombre de pasajero…"
                value={filters.passengers || ""}
                onChange={(event) => updateFilter("passengers", event.target.value)}
              />
            </label>

            <div className="service-email-filter-field service-email-filter-time-range">
              <span className="service-email-filter-label">Hora de salida</span>
              <div className="service-email-time-inputs">
                <input
                  type="time"
                  className="service-email-filter-input service-email-time-input"
                  placeholder="Desde"
                  value={filters.time_from || ""}
                  onChange={(event) => updateFilter("time_from", event.target.value)}
                />
                <span className="service-email-time-separator">—</span>
                <input
                  type="time"
                  className="service-email-filter-input service-email-time-input"
                  placeholder="Hasta"
                  value={filters.time_to || ""}
                  onChange={(event) => updateFilter("time_to", event.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="service-emails-table-shell service-email-monitor-shell">
        <table className="service-emails-table-modern service-email-solicitudes-table service-email-monitor-table">
          <thead>
            <tr>
              <th>Estado</th>
              <th>Solicitud</th>
              <th>Salida</th>
              {mode === "servicio" ? (
                <>
                  <th>Cobertura pax</th>
                  <th>Servicio agendado</th>
                </>
              ) : (
                <>
                  <th>Servicios</th>
                  <th>Conductor / estado</th>
                </>
              )}
              <th>Ruta</th>
              <th>Correo / Emisor</th>
              <th className="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading && visibleData.length === 0 ? (
              <tr>
                <td colSpan={8} className="service-emails-state-cell">
                  <div className="service-emails-loading">
                    <div className="spinner-pro" />
                    <span>Sincronizando solicitudes…</span>
                  </div>
                </td>
              </tr>
            ) : visibleData.length === 0 ? (
              <tr>
                <td colSpan={8} className="service-emails-state-cell">
                  <div className="service-emails-empty">
                    <p>No se encontraron solicitudes</p>
                    <span>
                      {hasFilters ? "Intenta ajustar los filtros" : "Aún no hay solicitudes registradas"}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              visibleData.map((item) => {
                const emailId = item.email_request_id ?? item.email_request;
                const hasRoute = Boolean(item.origin_text || item.destination_text);
                const assigned = getAssigned(item);
                const total = Math.max(1, item.passenger_count || 1);
                const remaining = getRemaining(item);
                const progress = Math.min(100, Math.round((assigned / total) * 100));
                const ops = resolveOpsStatus(item);
                const status = (item.email_status ?? "PENDIENTE") as ServiceEmailStatus;

                return (
                  <tr
                    key={item.id}
                    className={`service-emails-row service-email-monitor-row service-email-monitor-row--${ops}${
                      loading ? " is-refreshing" : ""
                    }`}
                  >
                    <td className="col-urgency">{renderOpsBadge(ops)}</td>

                    <td className="col-mail">
                      <div className="mail-cell">
                        <span className="mail-cell-id">#{item.id}</span>
                        <span className="mail-cell-subject">
                          {item.passenger_names?.trim() || `Solicitud #${item.id}`}
                        </span>
                        <span className="mail-cell-sender">
                          Correo #{emailId} · {item.email_status_label ?? STATUS_LABELS[status]}
                        </span>
                      </div>
                    </td>

                    <td className="col-service">
                      <div className="service-email-departure">
                        <strong>{formatTime(item.request_time)}</strong>
                        <span>{formatServiceDate(item.request_date)}</span>
                      </div>
                    </td>

                    {mode === "servicio" ? (
                      <>
                        <td className="col-solicitudes">
                          <div className="service-email-coverage">
                            <div className="service-email-coverage-head">
                              <span>
                                {assigned}/{total}
                              </span>
                              <span className={remaining > 0 ? "is-pending" : "is-done"}>
                                {remaining > 0 ? `${remaining} pend.` : "OK"}
                              </span>
                            </div>
                            <div
                              className="service-email-coverage-bar"
                              role="progressbar"
                              aria-valuenow={progress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              <span
                                className={`service-email-coverage-fill service-email-coverage-fill--${ops}`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="col-service-link">{renderServiceCell(item)}</td>
                      </>
                    ) : (
                      <>
                        <td className="col-service-ids">
                          <span className="service-ids-text" title={item.service_ids || ""}>
                            {item.service_ids?.trim() || "—"}
                          </span>
                        </td>
                        <td className="col-driver">{renderDriverCell(item)}</td>
                      </>
                    )}

                    <td className="col-route">
                      {hasRoute ? (
                        <span
                          className="route-compact"
                          title={`${item.origin_text} → ${item.destination_text}`}
                        >
                          {item.origin_text || "—"}
                          <span className="route-arrow">→</span>
                          {item.destination_text || "—"}
                        </span>
                      ) : (
                        <span className="route-empty">—</span>
                      )}
                    </td>

                    <td className="col-mail">
                      <div className="mail-cell">
                        <span className="mail-cell-subject" title={item.email_subject}>
                          {item.email_subject || "—"}
                        </span>
                        <span className="mail-cell-sender" title={item.email_sender_email}>
                          {item.email_sender_name ? `${item.email_sender_name} · ` : ""}
                          {item.email_sender_email || "—"}
                        </span>
                      </div>
                    </td>

                    <td className="col-actions">
                      <div className="srv-actions">
                        <BaseTableAction
                          icon={EditIcon}
                          title="Abrir correo"
                          onClick={() => onOpenEmail(emailId)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(ServiceEmailSolicitudesTable);
